import React from 'react';
import { X, InfoIcon } from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

const TransactionDetailsModal = ({ transaction, onClose }) => {
  if (!transaction) return null;

  const formatAddress = (shipping) => {
    if (!shipping || !shipping.address) return 'No address provided';
    const address = shipping.address;
    return `${address.street || ''}, ${address.state || ''}, ${address.country || ''}`;
  };

  // Calculate the platform fee for this transaction
  const platformFee = transaction.finalAmount - (transaction.vendorAmount || 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-medium">Transaction Details</h3>
          <button
            onClick={onClose}
            className="text-gray-500 bg-gray-50 hover:text-gray-700 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Status and reference */}
          <div className="flex flex-wrap items-start justify-between mb-6">
            <div>
              <h4 className="text-sm text-gray-500">Reference ID</h4>
              <p className="font-medium">{transaction.reference}</p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                ${transaction.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                  transaction.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    transaction.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'}`}>
                {transaction.status}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full 
                ${transaction.paymentMethod === 'PAYSTACK' ? 'bg-blue-100 text-blue-800' :
                  transaction.paymentMethod === 'WHATSAPP' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'}`}>
                {transaction.paymentMethod}
              </span>
            </div>
          </div>

          {/* Financial summary */}
          {/* Financial summary with detailed fee breakdown */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium text-gray-800 mb-3">Financial Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Customer paid:</span>
                <span className="font-semibold">₦{transaction.finalAmount.toLocaleString()}</span>
              </div>

              {/* Detailed fee breakdown */}
              {(transaction.metadata?.fees?.paystackTransactionFee || transaction.metadata?.fees?.platformFee) && (
                <div className="pl-4 border-l-2 border-gray-200 space-y-1">
                  {transaction.metadata?.fees?.paystackTransactionFee > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Paystack fee:</span>
                      <span>₦{transaction.metadata.fees.paystackTransactionFee.toLocaleString()}</span>
                    </div>
                  )}
                  {transaction.metadata?.fees?.platformFee > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Platform fee:</span>
                      <span>₦{transaction.metadata.fees.platformFee.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 border-t border-gray-200 flex justify-between">
                <span>Your earnings:</span>
                <span className="font-bold text-green-600">
                  ₦{transaction.vendorAmount ? transaction.vendorAmount.toLocaleString() : 'Calculating...'}
                </span>
              </div>
              {(transaction.paymentMethod !== 'WHATSAPP') && (
                <div className="pt-1 text-xs text-gray-500 flex items-center">
                  <InfoIcon className="w-3 h-3 mr-1" />
                  <span>Settlements are processed automatically to your bank account</span>
                </div>
              )}
            </div>
          </div>

          {/* Date */}
          <div className="mb-6">
            <h4 className="text-sm text-gray-500">Date</h4>
            <p className="font-medium">{formatDateTime(transaction.dateCreated)}</p>
          </div>

          {/* Customer details */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-2">Customer Information</h4>
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{transaction.customer?.name || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{transaction.customer?.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{transaction.customer?.email || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping details if available */}
          {transaction.metadata?.shipping && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-2">Shipping Details</h4>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Method</p>
                    <p className="font-medium">{transaction.metadata.shipping.method || 'Standard Shipping'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cost</p>
                    <p className="font-medium">
                      {transaction.metadata.shipping.cost ?
                        `₦${transaction.metadata.shipping.cost.toLocaleString()}` :
                        'Free'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{formatAddress(transaction.metadata.shipping)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Items purchased */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-2">Items Purchased</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transaction.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.name}
                        {item.variantName && <span className="text-gray-500 text-xs ml-1">({item.variantName})</span>}
                      </td>
                      <td>
                        {/* Add product type indicator */}
                        <span className={`px-2 py-1 text-xs rounded ${item.productType === 'DIGITAL' ? 'bg-purple-100 text-purple-800' :
                          item.productType === 'HYBRID' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                          {item.productType || 'PHYSICAL'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">₦{item.price.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">₦{(item.price * item.quantity).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  {/* Display subtotal */}
                  <tr>
                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                      Subtotal
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                      ₦{transaction.amount.toLocaleString()}
                    </td>
                  </tr>

                  {/* Display discount if available */}
                  {transaction.metadata?.discount && (
                    <tr>
                      <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                        Discount ({transaction.metadata.discount.code})
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-red-600 text-right">
                        -₦{transaction.metadata.discount.discountAmount.toLocaleString()}
                      </td>
                    </tr>
                  )}

                  {/* Shipping cost if available */}
                  {transaction.metadata?.shipping?.cost > 0 && (
                    <tr>
                      <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                        Shipping ({transaction.metadata.shipping.method || 'Standard'})
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                        ₦{transaction.metadata.shipping.cost.toLocaleString()}
                      </td>
                    </tr>
                  )}

                  {/* Final total */}
                  <tr>
                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-900 text-right">Total</td>
                    <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">₦{transaction.finalAmount.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes if available */}
          {transaction.metadata?.notes && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-2">Order Notes</h4>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm">{transaction.metadata.notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;